import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { logError, logInfo } from '../logger';
import * as turf from '@turf/turf';

const WFS_LAYERS_COLLECTION = 'wfs_layers';

/**
 * Retrieves objects from Firestore that intersect with a route buffer
 * @param {string} layerId - The layer ID to query
 * @param {Object} routeLineString - Turf.js linestring representing the route
 * @param {number} bufferDistance - Buffer distance in kilometers
 * @returns {Promise<Array>} - Array of filtered features
 */
export const getObjectsFromFirebase = async (layerId, routeLineString, bufferDistance = 0.5) => {
  try {
    // Create a buffer around the route
    const buffered = turf.buffer(routeLineString, bufferDistance, { units: 'kilometers' });
    
    // Get the layer document from Firestore
    const layerRef = doc(db, WFS_LAYERS_COLLECTION, layerId);
    const layerDoc = await getDoc(layerRef);
    
    if (!layerDoc.exists() || !layerDoc.data().features) {
      logInfo(`Layer ${layerId} not found in Firestore or has no features`);
      return [];
    }
    
    const layerData = layerDoc.data();
    const features = layerData.features || [];
    
    logInfo(`Retrieved ${features.length} features for layer ${layerId} from Firestore`);
    
    // Filter features that intersect with the route buffer
    const filteredFeatures = features.filter(feature => {
      try {
        if (!feature.geometry || !feature.geometry.coordinates) return false;
        
        let featureGeojson;
        switch (feature.geometry.type) {
          case 'Point':
            featureGeojson = turf.point(feature.geometry.coordinates);
            break;
          case 'LineString':
            featureGeojson = turf.lineString(feature.geometry.coordinates);
            break;
          case 'Polygon':
            featureGeojson = turf.polygon(feature.geometry.coordinates);
            break;
          case 'MultiPoint':
            featureGeojson = turf.multiPoint(feature.geometry.coordinates);
            break;
          case 'MultiLineString':
            featureGeojson = turf.multiLineString(feature.geometry.coordinates);
            break;
          case 'MultiPolygon':
            featureGeojson = turf.multiPolygon(feature.geometry.coordinates);
            break;
          default:
            return false;
        }
        
        // Check if feature intersects with buffer
        return turf.booleanIntersects(featureGeojson, buffered);
      } catch (error) {
        logError(`Error filtering feature:`, error);
        return false;
      }
    });
    
    logInfo(`Filtered to ${filteredFeatures.length} features that intersect with route buffer`);
    
    return filteredFeatures;
  } catch (error) {
    logError(`Failed to get objects from Firestore for layer ${layerId}:`, error);
    throw error;
  }
};